import React, { useState } from 'react';
import CustomSlider from '../../components/CustomSlider';
import useTranslation from '../../hooks/useTranslation';
import EffectActionBar from '../../components/effects/EffectActionBar';
import { 
  SunIcon, 
  SparklesIcon, 
  AdjustmentsHorizontalIcon,
  FireIcon,
  SwatchIcon,
  SunIcon as LightIcon,
  MoonIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

const AdjustmentsControls = ({ settings, onUpdate, onApply, onBack, hideBackButton = false, isMobile = false, activeGroup = null, onGroupChange = null }) => {
  const { t } = useTranslation();
  const [internalActiveGroup, setInternalActiveGroup] = useState(null); // Para mobile: qual grupo está ativo (estado interno)
  
  // Usar estado externo se fornecido, caso contrário usar interno
  const currentActiveGroup = isMobile && onGroupChange !== null ? activeGroup : internalActiveGroup;
  const setActiveGroup = isMobile && onGroupChange !== null ? onGroupChange : setInternalActiveGroup;
  
  // Verificar se todos os ajustes estão zerados
  const allAdjustmentsZero = 
    (settings.brightness || 0) === 0 &&
    (settings.contrast || 0) === 0 &&
    (settings.saturation || 0) === 0 &&
    (settings.sharpness || 0) === 0 &&
    (settings.temperature || 0) === 0 &&
    (settings.tint || 0) === 0 &&
    (settings.highlights || 0) === 0 &&
    (settings.shadows || 0) === 0 &&
    (settings.exposure || 0) === 0 &&
    (settings.redShadows || 0) === 0 &&
    (settings.redHighlights || 0) === 0 &&
    (settings.greenShadows || 0) === 0 &&
    (settings.greenHighlights || 0) === 0 &&
    (settings.blueShadows || 0) === 0 &&
    (settings.blueHighlights || 0) === 0;
  
  // Função para lidar com drag do knob
  const handleKnobDrag = (e, key, min, max, step) => {
    // Não chamar preventDefault em touch events passivos
    if (e.type === 'touchstart') {
      e.stopPropagation();
    } else {
      e.preventDefault();
    }
    
    const startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    const startValue = settings[key] || 0;
    const sliderWidth = 280; // Largura do slider
    
    const handleMove = (moveEvent) => {
      moveEvent.preventDefault();
      const currentX = moveEvent.type === 'mousemove' ? moveEvent.clientX : moveEvent.touches[0].clientX;
      const deltaX = currentX - startX;
      const deltaValue = (deltaX / sliderWidth) * (max - min);
      const newValue = Math.max(min, Math.min(max, startValue + deltaValue));
      
      // Arredondar para o step mais próximo
      const steppedValue = Math.round(newValue / step) * step;
      onUpdate({ [key]: steppedValue });
    };
    
    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);
  };

  // Função helper para criar sliders customizados (mobile) ou CustomSlider (desktop)
  const renderSlider = (label, key, min, max, step, color = 'blue', formatValue = null, gradient = null, thumbColor = null) => {
    const value = settings[key] || 0;

    if (isMobile) {
      // Mobile: Slider customizado com estrutura HTML correta
      const percentage = ((value - min) / (max - min)) * 100;
      
      return (
        <div className="range-slider-wrapper">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-normal text-white">{label}</label>
            {formatValue && (
              <span className="text-xs font-normal text-white px-2 py-1 rounded-md" style={{ backgroundColor: 'transparent' }}>
                {formatValue(value)}
              </span>
            )}
          </div>
          <div className="range-slider range-slider-horizontal" style={{ width: '100%' }}>
            <div className="range-bar">
              <div 
                className="range-bar-active" 
                style={{ 
                  width: `${percentage}%`,
                  backgroundColor: color === 'green' ? '#7eb715' : color
                }}
              ></div>
            </div>
            <div 
              className="range-knob-wrap range-knob-active-state" 
              style={{ left: `${percentage}%` }}
              onMouseDown={(e) => handleKnobDrag(e, key, min, max, step)}
              onTouchStart={(e) => handleKnobDrag(e, key, min, max, step)}
            >
              <div className="range-knob"></div>
              <div className="range-knob-label">{formatValue ? formatValue(value) : Math.round(value * 100)}</div>
            </div>
          </div>
        </div>
      );
    }

    // Desktop: CustomSlider
    return (
      <CustomSlider
        label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(newValue) => onUpdate({ [key]: newValue })}
        description=""
        unit="%"
        color={color}
        formatValue={formatValue}
        gradient={gradient}
        thumbColor={thumbColor}
      />
    );
  };

  // Grupos de ajustes para mobile
  const adjustmentGroups = [
    {
      id: 'brightness-contrast',
      icon: SunIcon,
      label: 'Brilho e Contraste',
      controls: [
        { key: 'brightness', label: t('adjustments.brightness'), min: -1, max: 1, step: 0.01, color: 'green', gradient: '#1f2937 0%, #f3f4f6 100%', thumbColor: '#000000' },
        { key: 'contrast', label: t('adjustments.contrast'), min: -1, max: 1, step: 0.01, color: 'green' }
      ]
    },
    {
      id: 'saturation',
      icon: SwatchIcon,
      label: 'Saturação',
      controls: [
        { key: 'saturation', label: t('adjustments.saturation'), min: -1, max: 1, step: 0.01, color: 'green' }
      ]
    },
    {
      id: 'sharpness',
      icon: SparklesIcon,
      label: 'Nitidez',
      controls: [
        { key: 'sharpness', label: t('adjustments.sharpness'), min: 0, max: 2, step: 0.1, color: 'green', formatValue: (v) => (v * 50).toFixed(0) }
      ]
    },
    {
      id: 'highlights-shadows',
      icon: AdjustmentsHorizontalIcon,
      label: 'Luzes e Sombras',
      controls: [
        { key: 'highlights', label: t('adjustments.highlights'), min: -1, max: 1, step: 0.01, color: 'green' },
        { key: 'shadows', label: t('adjustments.shadows'), min: -1, max: 1, step: 0.01, color: 'green' }
      ]
    },
    {
      id: 'temperature-tint',
      icon: FireIcon,
      label: 'Temperatura e Tonalidade',
      controls: [
        { key: 'temperature', label: t('adjustments.temperature'), min: -1, max: 1, step: 0.01, color: 'green', gradient: '#00c0ff 0%, #fff 50%, #ff9d00 100%' },
        { key: 'tint', label: t('adjustments.tint'), min: -1, max: 1, step: 0.01, color: 'green', gradient: '#d100d2 0%, #efefef 51%, #00d70f 100%' }
      ]
    },
    {
      id: 'rgb',
      icon: Squares2X2Icon,
      label: 'RGB',
      controls: [] // Layout especial com sliders verticais
    }
  ];

  // Renderizar controles RGB verticais customizados
  const renderRGBControls = () => {
    const rgbChannels = [
      { 
        color: '#ef4444', 
        shadowKey: 'redShadows', 
        highlightKey: 'redHighlights',
        label: 'R'
      },
      { 
        color: '#22c55e', 
        shadowKey: 'greenShadows', 
        highlightKey: 'greenHighlights',
        label: 'G'
      },
      { 
        color: '#3b82f6', 
        shadowKey: 'blueShadows', 
        highlightKey: 'blueHighlights',
        label: 'B'
      }
    ];

    return (
      <div className="flex justify-between items-center h-full px-6">
        {rgbChannels.map((channel, index) => (
          <React.Fragment key={channel.label}>
            {/* Espaço adicional entre canais R, G, B */}
            {index > 0 && <div style={{ width: '16px' }} />}
            
            {/* Sombras */}
            <div className="flex flex-col items-center">
              <MoonIcon className="w-5 h-5 mb-1" style={{ color: channel.color, opacity: 0.6 }} />
              <div className="range-slider-wrapper vertical" style={{ height: '140px', width: '40px' }}>
                {(() => {
                  const shadowValue = (settings[channel.shadowKey] || 0) * 100;
                  const shadowPercentage = ((shadowValue - (-100)) / (100 - (-100))) * 100;
                  
                  return (
                    <div className="range-slider range-slider-vertical" style={{ height: '120px', width: '4px' }}>
                      <div className="range-bar">
                        <div 
                          className="range-bar-active" 
                          style={{ 
                            height: `${shadowPercentage}%`,
                            backgroundColor: channel.color
                          }}
                        ></div>
                      </div>
                      <div 
                        className="range-knob-wrap range-knob-active-state" 
                        style={{ bottom: `${shadowPercentage}%` }}
                        onMouseDown={(e) => handleKnobDrag(e, channel.shadowKey, -1, 1, 0.01)}
                        onTouchStart={(e) => handleKnobDrag(e, channel.shadowKey, -1, 1, 0.01)}
                      >
                        <div className="range-knob"></div>
                        <div className="range-knob-label">{Math.round(shadowValue)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Espaço entre sombras e luzes do mesmo canal */}
            <div style={{ width: '12px' }} />

            {/* Luzes */}
            <div className="flex flex-col items-center">
              <LightIcon className="w-5 h-5 mb-1" style={{ color: channel.color }} />
              <div className="range-slider-wrapper vertical" style={{ height: '140px', width: '40px' }}>
                {(() => {
                  const highlightValue = (settings[channel.highlightKey] || 0) * 100;
                  const highlightPercentage = ((highlightValue - (-100)) / (100 - (-100))) * 100;
                  
                  return (
                    <div className="range-slider range-slider-vertical" style={{ height: '120px', width: '4px' }}>
                      <div className="range-bar">
                        <div 
                          className="range-bar-active" 
                          style={{ 
                            height: `${highlightPercentage}%`,
                            backgroundColor: channel.color
                          }}
                        ></div>
                      </div>
                      <div 
                        className="range-knob-wrap range-knob-active-state" 
                        style={{ bottom: `${highlightPercentage}%` }}
                        onMouseDown={(e) => handleKnobDrag(e, channel.highlightKey, -1, 1, 0.01)}
                        onTouchStart={(e) => handleKnobDrag(e, channel.highlightKey, -1, 1, 0.01)}
                      >
                        <div className="range-knob"></div>
                        <div className="range-knob-label">{Math.round(highlightValue)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Renderizar versão mobile
  if (isMobile) {
    // Se um grupo está ativo
    if (currentActiveGroup) {
      if (currentActiveGroup === 'rgb') {
        // Layout especial para RGB
        return (
          <div className="flex items-center justify-center" style={{ minHeight: '180px' }}>
            {renderRGBControls()}
          </div>
        );
      } else {
        // Mostrar controles do grupo ativo
        return (
          <>
            {adjustmentGroups
              .find(g => g.id === currentActiveGroup)
              ?.controls.map((control) => (
                <div key={control.key}>
                  {renderSlider(
                    control.label,
                    control.key,
                    control.min,
                    control.max,
                    control.step,
                    control.color,
                    control.formatValue,
                    control.gradient,
                    control.thumbColor
                  )}
                </div>
              ))}
          </>
        );
      }
    }
    
    // Mostrar grade de ícones
    return (
      <div className="grid grid-cols-3 gap-3">
        {adjustmentGroups.map((group) => {
          const Icon = group.icon;
          return (
            <button
              key={group.id}
              onClick={() => setActiveGroup(group.id)}
              className="flex flex-col items-center justify-center p-3 transition-opacity hover:opacity-70"
            >
              <Icon className="w-7 h-7 text-[#7eb715] mb-1.5" />
              <span className="text-[10px] text-gray-300 text-center leading-tight">{group.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Renderizar versão desktop (original)
  return (
    <div className="flex flex-col h-full">
      {/* Controles Simplificados */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-12 p-4">
          {/* Seção: Ajustes Básicos */}
          <div>
            <h4 className="text-sm font-normal text-white mb-3">
              {t('adjustments.basicAdjustments')}
            </h4>
            <div className="space-y-5">
              {renderSlider(t('adjustments.brightness'), 'brightness', -1, 1, 0.01, 'green', null, '#1f2937 0%, #f3f4f6 100%', '#000000')}
              {renderSlider(t('adjustments.exposure'), 'exposure', -2, 2, 0.1, 'green', (v) => (v * 50).toFixed(0), '#1f2937 0%, #f3f4f6 100%', '#000000')}
              {renderSlider(t('adjustments.contrast'), 'contrast', -1, 1, 0.01, 'green')}
              {renderSlider(t('adjustments.saturation'), 'saturation', -1, 1, 0.01, 'green')}
              {renderSlider(t('adjustments.sharpness'), 'sharpness', 0, 2, 0.1, 'green', (v) => (v * 50).toFixed(0))}
            </div>
          </div>

          {/* Seção: Cor */}
          <div>
            <h4 className="text-sm font-normal text-white mb-3">
              {t('adjustments.color')}
            </h4>
            <div className="space-y-5">
              {renderSlider(t('adjustments.temperature'), 'temperature', -1, 1, 0.01, 'green', null, '#00c0ff 0%, #fff 50%, #ff9d00 100%')}
              {renderSlider(t('adjustments.tint'), 'tint', -1, 1, 0.01, 'green', null, '#d100d2 0%, #efefef 51%, #00d70f 100%')}
              {renderSlider(t('adjustments.highlights'), 'highlights', -1, 1, 0.01, 'green')}
              {renderSlider(t('adjustments.shadows'), 'shadows', -1, 1, 0.01, 'green')}
            </div>
          </div>

          {/* Seção: Canais RGB */}
          <div>
            <h4 className="text-sm font-normal text-white mb-3">
              {t('adjustments.shadows')} & {t('adjustments.highlights')}
            </h4>
            <div className="space-y-5">
              {/* Vermelho */}
              <div className="space-y-3">
                {renderSlider(t('adjustments.shadows'), 'redShadows', -1, 1, 0.01, 'red')}
                {renderSlider(t('adjustments.highlights'), 'redHighlights', -1, 1, 0.01, 'red')}
              </div>

              {/* Verde */}
              <div className="space-y-3">
                {renderSlider(t('adjustments.shadows'), 'greenShadows', -1, 1, 0.01, 'emerald')}
                {renderSlider(t('adjustments.highlights'), 'greenHighlights', -1, 1, 0.01, 'emerald')}
              </div>

              {/* Azul */}
              <div className="space-y-3">
                {renderSlider(t('adjustments.shadows'), 'blueShadows', -1, 1, 0.01, 'blue')}
                {renderSlider(t('adjustments.highlights'), 'blueHighlights', -1, 1, 0.01, 'blue')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de ações padronizada */}
      <EffectActionBar
        onReset={() => {
          onUpdate({
            brightness: 0,
            contrast: 0,
            saturation: 0,
            sharpness: 0,
            temperature: 0,
            tint: 0,
            highlights: 0,
            shadows: 0,
            exposure: 0,
            redShadows: 0,
            redHighlights: 0,
            greenShadows: 0,
            greenHighlights: 0,
            blueShadows: 0,
            blueHighlights: 0,
          });
        }}
        onApply={() => {
          // Ao aplicar ajustes, resetar todos os valores para 0
          onApply();
          // Reset imediato após aplicar
          onUpdate({
            brightness: 0,
            contrast: 0,
            saturation: 0,
            sharpness: 0,
            temperature: 0,
            tint: 0,
            highlights: 0,
            shadows: 0,
            exposure: 0,
            redShadows: 0,
            redHighlights: 0,
            greenShadows: 0,
            greenHighlights: 0,
            blueShadows: 0,
            blueHighlights: 0,
          });
        }}
        isApplyDisabled={allAdjustmentsZero}
        resetText={t('effects.resetEffects')}
        applyText={t('effects.applyEffects')}
      />
    </div>
  );
};

export default AdjustmentsControls;
